import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Calculator, Home, ClipboardList, Tag, UtensilsCrossed, BookOpen,
  BarChart3, TrendingUp, PiggyBank, Briefcase, Package, ShoppingCart,
  LineChart, Layers, FileText, FlaskConical, Activity, TestTube, Shield,
  Box, Sparkles, ArrowRight, ShieldCheck, ArrowLeft, Lock,
} from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import ToolSelector from '../components/ToolSelector';
import CalculatorTutorial from '../components/CalculatorTutorial';

const FREE_TOOLS = [
  { id: 'business-calculator',   title: 'Business Calculator',      description: 'Cost, margin and break-even planning for a freeze-dry business.', icon: Calculator,    path: '/business' },
  { id: 'household-calculator',  title: 'Household Calculator',     description: 'Plan and size batches for home freeze-drying projects.',           icon: Home,          path: '/household-calculator' },
  { id: 'batch-tracker',         title: 'Batch Tracker',            description: 'Track per-tray progress through your freeze-dry cycle.',           icon: ClipboardList, path: '/batch-tracker' },
  { id: 'label-maker',           title: 'Label Maker',              description: 'Design and print labels for finished product.',                    icon: Tag,           path: '/label-maker' },
  { id: 'food-prep-resource',    title: 'Home Meal Prep Calculator',description: 'AI meal plan with Walmart-baseline shopping list, currency + region.',icon: UtensilsCrossed, path: '/food-prep-resource' },
  { id: 'recipe-center',         title: 'Community Recipe Book',    description: 'Shared library of AI freeze-dry recipes with photos and dietary tags.', icon: BookOpen,   path: '/recipe-center' },
];

const FREE_COMING = [];

const TIER1 = [
  { id: 'recipe-builder',       title: 'Recipe Builder',                       description: 'AI-modeled freeze-dry cycles, denaturing risk, macro-driven time predictions.',            icon: BookOpen,   path: '/recipes' },
  { id: 'batch-analyzer',       title: 'Batch Analyzer',                       description: 'Compare cost + throughput across saved recipes and find your best-margin batch.',       icon: BarChart3,  path: '/tools/batch-analyzer' },
  { id: 'production-forecast',  title: 'Production Forecast Tool',             description: 'Schedule batches on a monthly calendar with cost + machine-hour rollups.',             icon: TrendingUp, path: '/tools/production-forecast' },
  { id: 'waste-cost-optimizer', title: 'Waste & Cost Reduction Optimizer',     description: 'Claude-ranked substitutions that lower cost, cut salt / fat, and shorten cycles.',      icon: PiggyBank,  path: '/tools/waste-cost-optimizer' },
];

const TIER2 = [
  { id: 'business-planning',        title: 'Business Planning Tool',        description: 'Financial projections and growth strategies for operators.',        icon: Briefcase },
  { id: 'commercial-batch-planner', title: 'Commercial Batch Planner',      description: 'Plan and optimise large-scale commercial freeze-dry operations.',   icon: Package },
  { id: 'marketplace-intel',        title: 'Marketplace & Competitor Intel', description: 'Market analysis and competitive intelligence.',                     icon: ShoppingCart },
  { id: 'batch-log-trends',         title: 'Batch Log Trend Analysis',      description: 'Advanced analytics from your historical batch data.',               icon: LineChart },
  { id: 'multi-machine-manager',    title: 'Multi-Machine Manager',         description: 'Coordinate multiple freeze dryers from one dashboard.',             icon: Layers },
];

const TIER3 = [
  { id: 'sop-builder',           title: 'SOP Builder',                            description: 'Author, version and share Standard Operating Procedures.',                icon: FileText },
  { id: 'scientific-tools',      title: 'Scientific Tools Suite',                 description: 'Analytical tooling for R&D and quality control.',                         icon: FlaskConical },
  { id: 'realtime-production',   title: 'Real-Time Production Tools',             description: 'Live dashboards for in-progress production runs.',                        icon: Activity },
  { id: 'product-development',   title: 'Product Development & Formulation',      description: 'Design and optimise new freeze-dried product formulations.',              icon: TestTube },
  { id: 'compliance-gmp',        title: 'Compliance, GMP & Documentation',        description: 'Manage regulatory compliance and Good Manufacturing Practices.',         icon: Shield },
  { id: 'industrial-packaging',  title: 'Industrial Packaging & Labeling',        description: 'Design large-scale packaging and labeling systems.',                      icon: Box },
];

// ------------------------------------------------------------------
// Presentational tool card
// ------------------------------------------------------------------
const ToolCard = ({ tool, state, onOpen }) => {
  const Icon = tool.icon;
  const isAvailable = state === 'available';
  const isComingSoon = state === 'coming';
  const isLocked = state === 'locked';

  return (
    <Card
      className={`pro-card border-0 transition-all ${
        isAvailable ? 'hover:-translate-y-0.5 hover:shadow-lg cursor-pointer' : ''
      }`}
      onClick={isAvailable ? onOpen : undefined}
      data-testid={`tool-card-${tool.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div
            className={`h-12 w-12 rounded-xl flex items-center justify-center ${
              isAvailable
                ? 'bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {isLocked ? <Lock className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
          </div>
          {isComingSoon && (
            <span className="status-pill status-pill-admin">Coming soon</span>
          )}
          {isLocked && (
            <span className="status-pill status-pill-admin">Pro</span>
          )}
        </div>
        <h3 className="font-heading text-base font-extrabold tracking-tight text-foreground mb-1.5">
          {tool.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {tool.description}
        </p>
        {isAvailable && (
          <div className="mt-4 flex items-center text-sm font-semibold text-primary">
            Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ------------------------------------------------------------------
// Section header
// ------------------------------------------------------------------
const Section = ({ eyebrow, title, subtitle, children }) => (
  <section className="mb-12" data-testid={`calc-section-${eyebrow.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="mb-5">
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-2 text-foreground">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1 text-sm text-muted-foreground max-w-2xl">{subtitle}</p>
      )}
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </section>
);

// ------------------------------------------------------------------
// Page
// ------------------------------------------------------------------
const Calculators = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Top nav */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-sm font-semibold text-foreground/80 hover:text-foreground"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <h1 className="font-heading text-lg font-extrabold tracking-tight text-foreground">
            Calculators &amp; Tools
          </h1>
          <ThemeToggle />
        </div>
      </header>

      <ToolSelector />

      <main className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Hero */}
        <div className="mb-8">
          <span className="eyebrow">Workbench</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 text-foreground">
            Calculators, planners and worksheets.
          </h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Free tools for anyone freeze-drying at home or running a small business.
            Additional Pro tools unlock with a Tier 1+ subscription.
          </p>
        </div>

        {/* Tutorial */}
        <CalculatorTutorial
          storageKey="calculators-hub-v1"
          title="Using these tools"
          steps={[
            {
              title: 'Pick the right tool for the question',
              body: 'Costing a batch for sale? Business Calculator. Planning a pantry run at home? Household Calculator. Monitoring a live cycle? Batch Tracker.',
            },
            {
              title: 'Everything you enter stays on your account',
              body: 'Numbers you type are saved to your profile so you can come back later and refine them. Nothing is shared outside your account.',
            },
            {
              title: 'Outputs are estimates — not advice',
              body: 'These are educational tools. Cross-check any financial or production output with a qualified professional before making real business decisions.',
            },
          ]}
        />

        {/* Free / available now */}
        <Section
          eyebrow="Free"
          title="Available now"
          subtitle="No subscription needed — these three are the daily workhorses."
        >
          {FREE_TOOLS.map(t => (
            <ToolCard key={t.id} tool={t} state="available" onOpen={() => navigate(t.path)} />
          ))}
        </Section>

        {/* Free — coming soon */}
        {FREE_COMING.length > 0 && (
          <Section
            eyebrow="Roadmap"
            title="Also free — in build"
            subtitle="On the way. We'll email you when they're live if you're logged in."
          >
            {FREE_COMING.map(t => (
              <ToolCard key={t.id} tool={t} state="coming" />
            ))}
          </Section>
        )}

        {/* Tier 1 */}
        <Section
          eyebrow="Tier 1 Pro"
          title="Recipe & batch optimisation"
          subtitle="Move from ad-hoc runs to a repeatable operation. Free users get a preview — Pro/Admin gets the full AI tool."
        >
          {TIER1.map(t => (
            <ToolCard
              key={t.id}
              tool={t}
              state={t.path ? 'available' : 'locked'}
              onOpen={t.path ? () => navigate(t.path) : undefined}
            />
          ))}
        </Section>

        {/* Tier 2 */}
        <Section
          eyebrow="Tier 2 Pro"
          title="Business intelligence"
          subtitle="For operators running multiple machines or scaling a brand."
        >
          {TIER2.map(t => (
            <ToolCard key={t.id} tool={t} state="locked" />
          ))}
        </Section>

        {/* Tier 3 */}
        <Section
          eyebrow="Tier 3 Pro"
          title="Enterprise & compliance"
          subtitle="R&amp;D, GMP compliance, real-time production and industrial packaging."
        >
          {TIER3.map(t => (
            <ToolCard key={t.id} tool={t} state="locked" />
          ))}
        </Section>

        {/* CTA */}
        <Card className="border-0 overflow-hidden">
          <div className="bg-navy-spotlight text-background p-8 sm:p-10 relative">
            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-xl">
                <span className="pill-badge">
                  <Sparkles className="w-3.5 h-3.5" /> More on the roadmap
                </span>
                <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-4">
                  Serious about running a freeze-dry business?
                </h3>
                <p className="mt-2 text-background/70 text-sm max-w-md">
                  Tier 1 unlocks the recipe engine and batch analytics. Tier 2 adds forecasting
                  and multi-machine control. Tier 3 is for compliance-heavy commercial ops.
                </p>
              </div>
              <Button
                size="lg"
                className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap"
                onClick={() => navigate('/pricing')}
                data-testid="calc-pricing-cta"
              >
                See Pro plans
              </Button>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default Calculators;
